import React, { useCallback, useEffect, useRef, useState } from "react";
import {
  Box, Flex, Text, Button, Badge, Spinner, Center, HStack, VStack, Select, Input, SimpleGrid,
  Table, Thead, Tbody, Tr, Th, Td, Tabs, TabList, TabPanels, Tab, TabPanel, Switch,
  FormControl, FormLabel, FormHelperText, InputGroup, InputLeftAddon, InputRightAddon,
  Alert, AlertIcon, Progress, Collapse, useDisclosure, useToast,
  AlertDialog, AlertDialogBody, AlertDialogContent, AlertDialogFooter, AlertDialogHeader, AlertDialogOverlay,
} from "@chakra-ui/react";
import { RefreshCw, Save, ExternalLink, ChevronDown, ChevronUp } from "lucide-react";
import { useNavigate } from "react-router-dom";
import dayjs from "dayjs";
import "dayjs/locale/pt-br";
import {
  getComprasFabrica, getCompraFabrica, updateCompraFabrica, getCompraFabricaPrevia,
} from "../services/api";
import { formatBRL, formatDate, formatDateTime } from "../utils/formatters";

const ACCENT = "#F68633";
const VERDE = "#01695E";

const DIAS_SEMANA = [
  { v: "1", l: "Segunda-feira" }, { v: "2", l: "Terça-feira" }, { v: "3", l: "Quarta-feira" },
  { v: "4", l: "Quinta-feira" }, { v: "5", l: "Sexta-feira" }, { v: "6", l: "Sábado" }, { v: "7", l: "Domingo" },
];

const HORARIOS = Array.from({ length: 48 }, (_, i) =>
  `${String(Math.floor(i / 2)).padStart(2, "0")}:${i % 2 ? "30" : "00"}`);

const STATUS_PEDIDO = {
  enviado: { label: "Enviado", color: "green" },
  erro_envio: { label: "Erro no envio", color: "red" },
  sem_vendas: { label: "Sem vendas", color: "gray" },
  vazio: { label: "Nada a repor", color: "gray" },
  erro: { label: "Erro", color: "red" },
  gerando: { label: "Gerando", color: "blue" },
};

const STATUS_ATUALIZACAO = {
  pendente: { label: "Na fila", color: "gray" },
  processando: { label: "Atualizando", color: "blue" },
  concluida: { label: "Concluída", color: "green" },
  cancelada: { label: "Cancelada", color: "orange" },
  erro: { label: "Erro", color: "red" },
};

const nomeDia = (v) => DIAS_SEMANA.find((d) => d.v === String(v))?.l || "—";

const dataCurta = (valor) => {
  const d = dayjs(valor);
  return d.isValid() ? d.locale("pt-br").format("ddd DD/MM HH:mm") : "—";
};

const paraForm = (d) => ({
  ativo: !!d.config.ativo,
  dia_semana: String(d.config.dia_semana),
  hora_pedido: d.config.hora_pedido,
  pedido_minimo: String(d.config.pedido_minimo ?? 0),
  enviar_sem_vendas: !!d.config.enviar_sem_vendas,
  complemento_ativo: !!d.config.complemento_ativo,
  complemento_janela_dias: String(d.config.complemento_janela_dias),
  complemento_min_vendas: String(d.config.complemento_min_vendas),
  complemento_estoque_maximo: String(d.config.complemento_estoque_maximo),
  complemento_ordem: d.config.complemento_ordem,
  prazo_ml_automatico: !!d.config.prazo_ml_automatico,
  dias_entrega: String(d.config.dias_entrega),
  folga_despacho_dias: String(d.config.folga_despacho_dias),
  whatsapp: d.fornecedor.whatsapp || "",
});

const numero = (v) => Number(String(v ?? "").replace(",", "."));

const paraPayload = (f) => ({
  ativo: f.ativo,
  dia_semana: Number(f.dia_semana),
  hora_pedido: f.hora_pedido,
  pedido_minimo: numero(f.pedido_minimo),
  enviar_sem_vendas: f.enviar_sem_vendas,
  complemento_ativo: f.complemento_ativo,
  complemento_janela_dias: numero(f.complemento_janela_dias),
  complemento_min_vendas: numero(f.complemento_min_vendas),
  complemento_estoque_maximo: numero(f.complemento_estoque_maximo),
  complemento_ordem: f.complemento_ordem,
  prazo_ml_automatico: f.prazo_ml_automatico,
  dias_entrega: numero(f.dias_entrega),
  folga_despacho_dias: numero(f.folga_despacho_dias),
  whatsapp: String(f.whatsapp || "").replace(/\D/g, "") || null,
});

function Kpi({ label, valor, ajuda, cor = VERDE }) {
  return (
    <Box bg="white" borderRadius="lg" boxShadow="sm" p={{ base: 3, md: 4 }} borderTop="3px solid" borderTopColor={cor} minW={0}>
      <Text fontSize="xs" color="gray.500" textTransform="uppercase" fontWeight="bold" letterSpacing="wide" noOfLines={1}>
        {label}
      </Text>
      <Text fontSize={{ base: "lg", md: "2xl" }} fontWeight="bold" color={cor} lineHeight="1.2" mt={1} noOfLines={1}>
        {valor}
      </Text>
      {ajuda && <Text fontSize="xs" color="gray.500" mt={1} noOfLines={2}>{ajuda}</Text>}
    </Box>
  );
}

function Secao({ titulo, descricao, children }) {
  return (
    <Box borderWidth="1px" borderColor="gray.200" borderRadius="lg" p={4} bg="white">
      <Text fontWeight="bold" color={VERDE}>{titulo}</Text>
      {descricao && <Text fontSize="xs" color="gray.500" mb={3}>{descricao}</Text>}
      <VStack align="stretch" spacing={4} mt={descricao ? 0 : 3}>{children}</VStack>
    </Box>
  );
}

function Chave({ label, ajuda, isChecked, onChange }) {
  return (
    <FormControl display="flex" alignItems="flex-start" justifyContent="space-between" gap={3}>
      <Box>
        <FormLabel mb={0} fontSize="sm">{label}</FormLabel>
        {ajuda && <Text fontSize="xs" color="gray.500">{ajuda}</Text>}
      </Box>
      <Switch colorScheme="orange" size="lg" isChecked={isChecked} onChange={(e) => onChange(e.target.checked)} />
    </FormControl>
  );
}

function CampoNumero({ label, ajuda, valor, onChange, sufixo, prefixo, decimal = false }) {
  return (
    <FormControl>
      <FormLabel fontSize="sm" mb={1}>{label}</FormLabel>
      <InputGroup size="sm">
        {prefixo && <InputLeftAddon>{prefixo}</InputLeftAddon>}
        <Input
          type="text"
          inputMode={decimal ? "decimal" : "numeric"}
          bg="white"
          value={valor}
          onChange={(e) => onChange(e.target.value.replace(decimal ? /[^\d.,]/g : /\D/g, ""))}
        />
        {sufixo && <InputRightAddon>{sufixo}</InputRightAddon>}
      </InputGroup>
      {ajuda && <FormHelperText fontSize="xs">{ajuda}</FormHelperText>}
    </FormControl>
  );
}

function TabelaItens({ colunas, linhas, vazio }) {
  if (!linhas?.length) {
    return <Text fontSize="sm" color="gray.500" py={2}>{vazio}</Text>;
  }
  return (
    <Box overflowX="auto" borderWidth="1px" borderColor="gray.100" borderRadius="md">
      <Table size="sm">
        <Thead bg="gray.50">
          <Tr>{colunas.map((c) => <Th key={c.label} isNumeric={c.numerico} whiteSpace="nowrap">{c.label}</Th>)}</Tr>
        </Thead>
        <Tbody>
          {linhas.map((l, i) => (
            <Tr key={i}>
              {colunas.map((c) => (
                <Td key={c.label} isNumeric={c.numerico} whiteSpace={c.quebra ? "normal" : "nowrap"} fontSize="sm">
                  {c.render(l)}
                </Td>
              ))}
            </Tr>
          ))}
        </Tbody>
      </Table>
    </Box>
  );
}

const COLUNAS_VENDAS = [
  { label: "Pedido", render: (i) => i.pedido || "—" },
  { label: "Código", render: (i) => <Text fontWeight="semibold">{i.codigo_fornecedor}</Text> },
  { label: "Produto", quebra: true, render: (i) => <Text noOfLines={2} minW="180px">{i.nome_produto}</Text> },
  { label: "Qtd", numerico: true, render: (i) => i.quantidade },
  { label: "Unit.", numerico: true, render: (i) => formatBRL(i.preco) },
  { label: "Valor", numerico: true, render: (i) => formatBRL(i.valor) },
];

const COLUNAS_REPOSICAO = [
  { label: "Código", render: (i) => <Text fontWeight="semibold">{i.codigo_fornecedor}</Text> },
  { label: "Produto", quebra: true, render: (i) => <Text noOfLines={2} minW="180px">{i.nome_produto}</Text> },
  { label: "Vendidos", numerico: true, render: (i) => i.vendidos },
  { label: "Estoque", numerico: true, render: (i) => i.estoque_taga },
  { label: "A caminho", numerico: true, render: (i) => i.em_transito },
  { label: "Qtd", numerico: true, render: (i) => <Text fontWeight="bold">{i.quantidade}</Text> },
  { label: "Valor", numerico: true, render: (i) => formatBRL(i.valor) },
];

const COLUNAS_RANKING = [
  { label: "Código", render: (r) => r.codigo_fornecedor },
  { label: "Vendidos", numerico: true, render: (r) => r.vendidos },
  { label: "Estoque", numerico: true, render: (r) => r.estoque_taga },
  { label: "A caminho", numerico: true, render: (r) => r.em_transito },
  { label: "Comprar", numerico: true, render: (r) => r.comprar },
  {
    label: "Situação",
    render: (r) => (r.no_teto
      ? <Badge colorScheme="gray">no teto</Badge>
      : r.comprar > 0 ? <Badge colorScheme="green">entra</Badge> : <Badge colorScheme="yellow">sobrou</Badge>),
  },
];

const ComprasFabrica = () => {
  const toast = useToast();
  const navigate = useNavigate();
  const cancelRef = useRef();
  const ranking = useDisclosure();

  const [fornecedores, setFornecedores] = useState([]);
  const [fornecedorId, setFornecedorId] = useState(null);
  const [dados, setDados] = useState(null);
  const [form, setForm] = useState(null);
  const [inicial, setInicial] = useState(null);
  const [carregando, setCarregando] = useState(true);
  const [salvando, setSalvando] = useState(false);
  const [confirmar, setConfirmar] = useState(null);
  const [aba, setAba] = useState(0);
  const [previa, setPrevia] = useState(null);
  const [carregandoPrevia, setCarregandoPrevia] = useState(false);

  const carregarDados = useCallback(async (id, resetarForm = true) => {
    const d = await getCompraFabrica(id);
    if (!d) {
      toast({ title: "Não foi possível carregar a configuração.", status: "error", duration: 4000, isClosable: true });
      return;
    }
    setDados(d);
    if (resetarForm) {
      const f = paraForm(d);
      setForm(f);
      setInicial(f);
    }
  }, [toast]);

  const carregar = useCallback(async () => {
    setCarregando(true);
    try {
      const lista = (await getComprasFabrica()) || [];
      setFornecedores(lista);
      const id = fornecedorId && lista.some((f) => f.fornecedor_id === fornecedorId)
        ? fornecedorId
        : lista[0]?.fornecedor_id ?? null;
      setFornecedorId(id);
      if (id) {
        await carregarDados(id);
      } else {
        setDados(null);
      }
    } finally {
      setCarregando(false);
    }
  }, [fornecedorId, carregarDados]);

  useEffect(() => {
    carregar();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const carregarPrevia = useCallback(async () => {
    if (!fornecedorId) return;
    setCarregandoPrevia(true);
    try {
      setPrevia(await getCompraFabricaPrevia(fornecedorId));
    } finally {
      setCarregandoPrevia(false);
    }
  }, [fornecedorId]);

  useEffect(() => {
    if (aba === 1 && !previa) carregarPrevia();
  }, [aba, previa, carregarPrevia]);

  // Acompanha a atualização dos anúncios no ML sem mexer no que está sendo editado.
  const atualizandoMl = dados?.atualizacoes?.some((a) => a.status === "pendente" || a.status === "processando");
  useEffect(() => {
    if (!atualizandoMl || !fornecedorId) return undefined;
    const timer = setInterval(() => carregarDados(fornecedorId, false), 5000);
    return () => clearInterval(timer);
  }, [atualizandoMl, fornecedorId, carregarDados]);

  const trocarFornecedor = async (id) => {
    setFornecedorId(id);
    setPrevia(null);
    setCarregando(true);
    try {
      await carregarDados(id);
    } finally {
      setCarregando(false);
    }
  };

  const set = (campo) => (valor) => setForm((f) => ({ ...f, [campo]: valor }));
  const alterado = form && inicial && JSON.stringify(form) !== JSON.stringify(inicial);

  const pedirSalvar = () => {
    const avisos = [];
    if (form.ativo && !inicial.ativo) {
      avisos.push(`O pedido passa a sair sozinho toda ${nomeDia(form.dia_semana).toLowerCase()} às ${form.hora_pedido} para o WhatsApp ${form.whatsapp || "(vazio!)"}: vendas em crossdocking + reposição até ${formatBRL(numero(form.pedido_minimo))}.`);
    }
    if (form.prazo_ml_automatico && !inicial.prazo_ml_automatico) {
      avisos.push("O prazo dos anúncios no Mercado Livre passa a ser recalculado sozinho, e a atualização dos anúncios começa agora.");
    }
    if (avisos.length) {
      setConfirmar(avisos);
    } else {
      salvar();
    }
  };

  const salvar = async () => {
    setConfirmar(null);
    setSalvando(true);
    try {
      const res = await updateCompraFabrica(fornecedorId, paraPayload(form));
      if (res?.dados) {
        setDados(res.dados);
        const f = paraForm(res.dados);
        setForm(f);
        setInicial(f);
      }
      setPrevia(null);
      const prazo = res?.prazo;
      toast({
        title: res?.message || "Configuração salva.",
        description: prazo?.disparado ? `Prazo do ML: ${prazo.dias} dias — atualizando os anúncios.` : undefined,
        status: "success",
        duration: 5000,
        isClosable: true,
      });
    } catch (error) {
      const erros = error?.response?.data?.errors;
      const primeiro = erros ? Object.values(erros)[0]?.[0] : null;
      toast({
        title: "Não foi possível salvar.",
        description: primeiro || error?.response?.data?.message || "Tente de novo.",
        status: "error",
        duration: 6000,
        isClosable: true,
      });
    } finally {
      setSalvando(false);
    }
  };

  if (carregando && !dados) {
    return (
      <Box ml={{ base: 0, md: "64px" }} pt="70px" px={{ base: 3, md: 6 }} pb={10} minH="100vh" bg="gray.50">
        <Center py={20}><Spinner size="lg" color="orange.400" /></Center>
      </Box>
    );
  }

  const nome = dados?.fornecedor?.nome || "";
  const prazo = dados?.prazo;
  const ultimoPedido = dados?.pedidos?.[0];

  return (
    <Box ml={{ base: 0, md: "64px" }} pt="70px" px={{ base: 3, md: 6 }} pb={10} minH="100vh" bg="gray.50">
      <Flex justify="space-between" align={{ base: "stretch", md: "center" }} mb={3} gap={3} direction={{ base: "column", md: "row" }}>
        <Box>
          <Text fontSize="2xl" fontWeight="bold" color={VERDE}>Compras de Fábrica</Text>
          <Text fontSize="sm" color="gray.500">
            Pedido semanal com mínimo, reposição pelos mais vendidos e prazo automático no Mercado Livre.
          </Text>
        </Box>
        <HStack spacing={2}>
          {fornecedores.length > 1 && (
            <Select size="sm" bg="white" maxW="220px" value={fornecedorId ?? ""} onChange={(e) => trocarFornecedor(Number(e.target.value))}>
              {fornecedores.map((f) => <option key={f.fornecedor_id} value={f.fornecedor_id}>{f.nome}</option>)}
            </Select>
          )}
          <Button size="sm" leftIcon={<RefreshCw size={14} />} onClick={carregar} isLoading={carregando}
            bg={ACCENT} color="white" _hover={{ bg: "#e07728" }} flexShrink={0}>
            Atualizar
          </Button>
        </HStack>
      </Flex>

      {!dados ? (
        <Alert status="info" borderRadius="lg">
          <AlertIcon />
          Nenhum fornecedor configurado como compra de fábrica.
        </Alert>
      ) : (
        <>
          <SimpleGrid columns={{ base: 2, lg: 4 }} spacing={{ base: 2, md: 4 }} mb={4}>
            <Kpi
              label={`Pedido ${nome}`}
              valor={dados.config.ativo ? "Automático" : "Desligado"}
              ajuda={`Próximo: ${dataCurta(prazo?.proximo_pedido)}`}
              cor={dados.config.ativo ? VERDE : "#718096"}
            />
            <Kpi label="Pedido mínimo" valor={formatBRL(dados.config.pedido_minimo)}
              ajuda={ultimoPedido ? `Último: ${formatDate(ultimoPedido.ciclo)} · ${STATUS_PEDIDO[ultimoPedido.status]?.label || ultimoPedido.status}` : "Nenhum pedido ainda"}
              cor={ACCENT} />
            <Kpi
              label="Prazo no ML agora"
              valor={dados.config.prazo_ml_automatico ? `${prazo?.vigente} dias` : "Manual"}
              ajuda={dados.config.prazo_ml_automatico
                ? (prazo?.no_fornecedor !== prazo?.vigente ? `Gravado: ${prazo?.no_fornecedor ?? "sem prazo"} (ajusta em até 30 min)` : "Dias corridos a partir da compra")
                : `Gravado: ${prazo?.no_fornecedor ?? "sem prazo"} dias`}
              cor={dados.config.prazo_ml_automatico ? "#2B6CB0" : "#718096"}
            />
            <Kpi label="Anúncios"
              valor={`${dados.anuncios.ativos} ativos`}
              ajuda={`${dados.anuncios.pausados} pausados · ${dados.anuncios.com_estoque_taga} com estoque TAGA`}
              cor="#805AD5" />
          </SimpleGrid>

          <Box bg="white" borderRadius="lg" boxShadow="sm">
            <Tabs colorScheme="orange" index={aba} onChange={setAba} isLazy>
              <TabList px={{ base: 1, md: 4 }} overflowX="auto" overflowY="hidden" whiteSpace="nowrap">
                <Tab fontWeight="bold" fontSize="sm">Configuração</Tab>
                <Tab fontWeight="bold" fontSize="sm">Prévia do pedido</Tab>
                <Tab fontWeight="bold" fontSize="sm">Prazo no ML</Tab>
                <Tab fontWeight="bold" fontSize="sm">Histórico</Tab>
              </TabList>
              <TabPanels>
                {/* Configuração */}
                <TabPanel px={{ base: 3, md: 4 }} py={4}>
                  <SimpleGrid columns={{ base: 1, lg: 3 }} spacing={4}>
                    <Secao titulo="Pedido semanal" descricao="Vendas em crossdocking da semana, enviadas num pedido só.">
                      <Chave label="Enviar o pedido automaticamente" isChecked={form.ativo} onChange={set("ativo")}
                        ajuda="Desligado: nenhum pedido sai sozinho (e o fornecedor segue fora das listas diárias)." />
                      <SimpleGrid columns={2} spacing={3}>
                        <FormControl>
                          <FormLabel fontSize="sm" mb={1}>Dia</FormLabel>
                          <Select size="sm" bg="white" value={form.dia_semana} onChange={(e) => set("dia_semana")(e.target.value)}>
                            {DIAS_SEMANA.map((d) => <option key={d.v} value={d.v}>{d.l}</option>)}
                          </Select>
                        </FormControl>
                        <FormControl>
                          <FormLabel fontSize="sm" mb={1}>Horário (corte)</FormLabel>
                          <Select size="sm" bg="white" value={form.hora_pedido} onChange={(e) => set("hora_pedido")(e.target.value)}>
                            {HORARIOS.map((h) => <option key={h} value={h}>{h}</option>)}
                          </Select>
                        </FormControl>
                      </SimpleGrid>
                      <CampoNumero label="Pedido mínimo" prefixo="R$" decimal valor={form.pedido_minimo} onChange={set("pedido_minimo")}
                        ajuda="Valor pelo preço de compra cadastrado (com IPI)." />
                      <FormControl>
                        <FormLabel fontSize="sm" mb={1}>WhatsApp do fornecedor</FormLabel>
                        <Input size="sm" bg="white" inputMode="numeric" placeholder="5549999999999" value={form.whatsapp}
                          onChange={(e) => set("whatsapp")(e.target.value.replace(/\D/g, "").slice(0, 15))} />
                        <FormHelperText fontSize="xs">Só recebe o pedido — não entra no envio de cotação de furo.</FormHelperText>
                      </FormControl>
                      <Chave label="Semana sem venda: enviar só a reposição" isChecked={form.enviar_sem_vendas} onChange={set("enviar_sem_vendas")}
                        ajuda="Desligado: sem venda em crossdocking, não sai pedido." />
                    </Secao>

                    <Secao titulo="Complemento até o mínimo" descricao="Se as vendas não batem o mínimo, completa com reposição de estoque.">
                      <Chave label="Completar com os mais vendidos" isChecked={form.complemento_ativo} onChange={set("complemento_ativo")} />
                      <SimpleGrid columns={2} spacing={3}>
                        <CampoNumero label="Período de vendas" sufixo="dias" valor={form.complemento_janela_dias} onChange={set("complemento_janela_dias")} />
                        <CampoNumero label="Mínimo vendido" sufixo="un" valor={form.complemento_min_vendas} onChange={set("complemento_min_vendas")} />
                      </SimpleGrid>
                      <CampoNumero label="Estoque máximo por item" sufixo="un" valor={form.complemento_estoque_maximo} onChange={set("complemento_estoque_maximo")}
                        ajuda="Estoque TAGA + o que já está a caminho não passa disso." />
                      <FormControl>
                        <FormLabel fontSize="sm" mb={1}>Prioridade</FormLabel>
                        <Select size="sm" bg="white" value={form.complemento_ordem} onChange={(e) => set("complemento_ordem")(e.target.value)}>
                          <option value="mais_vendidos">Mais vendidos (empate: menor estoque)</option>
                          <option value="menor_estoque">Menor estoque (empate: mais vendidos)</option>
                        </Select>
                        <FormHelperText fontSize="xs">Uma unidade por item a cada volta, até bater o mínimo.</FormHelperText>
                      </FormControl>
                    </Secao>

                    <Secao titulo="Prazo no Mercado Livre" descricao="O ML conta em dias corridos a partir da compra.">
                      <Chave label="Recalcular o prazo sozinho" isChecked={form.prazo_ml_automatico} onChange={set("prazo_ml_automatico")}
                        ajuda="Cai à meia-noite e sobe 2 h antes do corte. Item com estoque TAGA fica sem prazo." />
                      <SimpleGrid columns={2} spacing={3}>
                        <CampoNumero label="Chega em" sufixo="dias" valor={form.dias_entrega} onChange={set("dias_entrega")}
                          ajuda="Dias corridos após o pedido." />
                        <CampoNumero label="Folga p/ despachar" sufixo="dias" valor={form.folga_despacho_dias} onChange={set("folga_despacho_dias")}
                          ajuda="Depois da chegada." />
                      </SimpleGrid>
                      <Text fontSize="xs" color="gray.500">
                        Com pedido {nomeDia(form.dia_semana).toLowerCase()} {form.hora_pedido}: quem compra logo depois do corte
                        espera {7 + numero(form.dias_entrega) + numero(form.folga_despacho_dias)} dias; logo antes,
                        {" "}{numero(form.dias_entrega) + numero(form.folga_despacho_dias)} dias.
                      </Text>
                    </Secao>
                  </SimpleGrid>

                  <Flex justify="flex-end" mt={4} gap={2} direction={{ base: "column-reverse", sm: "row" }}>
                    {alterado && (
                      <Button size="sm" variant="ghost" onClick={() => setForm(inicial)}>Desfazer</Button>
                    )}
                    <Button leftIcon={<Save size={16} />} colorScheme="green" onClick={pedirSalvar}
                      isLoading={salvando} isDisabled={!alterado || salvando}>
                      Salvar
                    </Button>
                  </Flex>
                </TabPanel>

                {/* Prévia */}
                <TabPanel px={{ base: 3, md: 4 }} py={4}>
                  <Flex justify="space-between" align="center" mb={3} gap={2} wrap="wrap">
                    <Text fontSize="sm" color="gray.600">
                      O pedido que sairia agora. Só leitura — nada é gravado nem enviado.
                    </Text>
                    <Button size="sm" variant="outline" leftIcon={<RefreshCw size={14} />} onClick={carregarPrevia} isLoading={carregandoPrevia}>
                      Recalcular
                    </Button>
                  </Flex>

                  {carregandoPrevia && !previa ? (
                    <Center py={10}><Spinner color="orange.400" /></Center>
                  ) : !previa ? (
                    <Text fontSize="sm" color="gray.500">Não foi possível montar a prévia.</Text>
                  ) : (
                    <VStack align="stretch" spacing={4}>
                      <SimpleGrid columns={{ base: 2, md: 4 }} spacing={{ base: 2, md: 3 }}>
                        <Kpi label="Vendas" valor={formatBRL(previa.valor_vendas)} ajuda={`${previa.itens_venda.length} item(ns)`} />
                        <Kpi label="Reposição" valor={formatBRL(previa.valor_complemento)} ajuda={`${previa.complemento.length} item(ns)`} cor={ACCENT} />
                        <Kpi label="Total" valor={formatBRL(previa.valor_total)} cor={previa.abaixo_minimo ? "#C53030" : VERDE}
                          ajuda={previa.abaixo_minimo ? "abaixo do mínimo" : "atinge o mínimo"} />
                        <Kpi label="Mínimo" valor={formatBRL(previa.pedido_minimo)} cor="#718096" />
                      </SimpleGrid>

                      {previa.sai_pedido ? (
                        <Alert status={previa.abaixo_minimo ? "warning" : "success"} borderRadius="md" fontSize="sm">
                          <AlertIcon />
                          {previa.abaixo_minimo
                            ? "Sairia abaixo do mínimo: não há mais item no ranking para completar. O comprador é avisado no WhatsApp."
                            : `Sairia um pedido de ${formatBRL(previa.valor_total)}.`}
                        </Alert>
                      ) : (
                        <Alert status="info" borderRadius="md" fontSize="sm">
                          <AlertIcon />
                          Nenhuma venda em crossdocking pendente — hoje não sairia pedido.
                        </Alert>
                      )}

                      <Box>
                        <Text fontWeight="bold" fontSize="sm" mb={2}>Vendas em crossdocking</Text>
                        <TabelaItens colunas={COLUNAS_VENDAS} linhas={previa.itens_venda} vazio="Nenhuma venda pendente de compra." />
                      </Box>

                      <Box>
                        <Text fontWeight="bold" fontSize="sm" mb={2}>Reposição</Text>
                        <TabelaItens colunas={COLUNAS_REPOSICAO} linhas={previa.complemento}
                          vazio={previa.valor_vendas >= previa.pedido_minimo ? "As vendas já batem o mínimo." : "Sem reposição."} />
                      </Box>

                      {previa.simulacao_sem_vendas && (
                        <Box borderWidth="1px" borderColor="orange.100" bg="orange.50" borderRadius="md" p={3}>
                          <Text fontWeight="bold" fontSize="sm">Simulação: reposição para o mínimo inteiro</Text>
                          <Text fontSize="xs" color="gray.600" mb={2}>
                            Como não há venda, mostra o que entraria de reposição se o pedido saísse só com ela
                            ({formatBRL(previa.simulacao_sem_vendas.valor)}).
                          </Text>
                          <TabelaItens colunas={COLUNAS_REPOSICAO} linhas={previa.simulacao_sem_vendas.itens}
                            vazio="Nenhum item com venda no período para repor." />
                        </Box>
                      )}

                      {(previa.ranking?.length > 0 || previa.simulacao_sem_vendas?.ranking?.length > 0) && (
                        <Box>
                          <Button size="xs" variant="ghost" onClick={ranking.onToggle}
                            rightIcon={ranking.isOpen ? <ChevronUp size={14} /> : <ChevronDown size={14} />}>
                            Ranking de mais vendidos usado
                          </Button>
                          <Collapse in={ranking.isOpen} animateOpacity>
                            <Box mt={2}>
                              <TabelaItens colunas={COLUNAS_RANKING}
                                linhas={previa.ranking?.length ? previa.ranking : previa.simulacao_sem_vendas?.ranking}
                                vazio="Sem ranking." />
                            </Box>
                          </Collapse>
                        </Box>
                      )}
                    </VStack>
                  )}
                </TabPanel>

                {/* Prazo no ML */}
                <TabPanel px={{ base: 3, md: 4 }} py={4}>
                  {!dados.config.prazo_ml_automatico && (
                    <Alert status="info" borderRadius="md" fontSize="sm" mb={3}>
                      <AlertIcon />
                      Prazo automático desligado: os anúncios ficam com o último prazo gravado
                      ({prazo?.no_fornecedor ?? "sem prazo"}). A tabela mostra como ficaria ligado.
                    </Alert>
                  )}
                  <Text fontSize="sm" color="gray.600" mb={3}>
                    Prazo do anúncio para quem compra em cada dia (dias corridos até o despacho).
                    Quem compra entre 2 h antes do corte e o corte vê o prazo longo e recebe antes do prometido.
                  </Text>
                  <TabelaItens
                    colunas={[
                      { label: "Compra", render: (r) => <Text fontWeight="semibold">{r.compra}</Text> },
                      { label: "Prazo ML", numerico: true, render: (r) => <Badge colorScheme="blue" fontSize="sm">{r.dias} dias</Badge> },
                      { label: "Pedido", render: (r) => r.pedido },
                      { label: "Chega", render: (r) => r.chegada },
                      { label: "Despacha", render: (r) => r.despacho },
                    ]}
                    linhas={prazo?.tabela}
                    vazio="—"
                  />

                  <Flex align="center" justify="space-between" mt={6} mb={2}>
                    <Text fontWeight="bold" fontSize="sm">Atualizações dos anúncios</Text>
                    {atualizandoMl && <Badge colorScheme="blue">atualizando…</Badge>}
                  </Flex>
                  {!dados.atualizacoes?.length ? (
                    <Text fontSize="sm" color="gray.500">Nenhuma atualização ainda.</Text>
                  ) : (
                    <VStack align="stretch" spacing={2}>
                      {dados.atualizacoes.map((a) => {
                        const st = STATUS_ATUALIZACAO[a.status] || { label: a.status, color: "gray" };
                        const ativa = a.status === "pendente" || a.status === "processando";
                        const pct = a.total_anuncios > 0
                          ? Math.min(100, Math.round((a.processados / a.total_anuncios) * 100))
                          : (a.status === "concluida" ? 100 : 0);
                        return (
                          <Box key={a.id} p={3} borderWidth="1px" borderRadius="md" bg="gray.50">
                            <Flex justify="space-between" align="center" gap={2} wrap="wrap" mb={1}>
                              <HStack spacing={2}>
                                <Badge colorScheme="purple">{a.dias ? `${a.dias} dias` : "sem prazo"}</Badge>
                                <Badge colorScheme={st.color}>{st.label}</Badge>
                              </HStack>
                              <Text fontSize="xs" color="gray.600">
                                {a.processados}/{a.total_anuncios} ({pct}%){a.falhas > 0 ? ` · ${a.falhas} falhas` : ""}
                              </Text>
                            </Flex>
                            <Progress value={pct} size="sm" colorScheme={st.color} borderRadius="md" hasStripe={ativa} isAnimated={ativa} />
                            <Text fontSize="xs" color="gray.500" mt={1}>
                              {formatDateTime(a.created_at, "")}{a.erro ? ` · Erro: ${String(a.erro).slice(0, 150)}` : ""}
                            </Text>
                          </Box>
                        );
                      })}
                    </VStack>
                  )}
                </TabPanel>

                {/* Histórico */}
                <TabPanel px={{ base: 3, md: 4 }} py={4}>
                  <TabelaItens
                    colunas={[
                      { label: "Data", render: (p) => formatDate(p.ciclo) },
                      {
                        label: "Status",
                        render: (p) => (
                          <HStack spacing={1}>
                            <Badge colorScheme={STATUS_PEDIDO[p.status]?.color || "gray"}>{STATUS_PEDIDO[p.status]?.label || p.status}</Badge>
                            {p.abaixo_minimo && <Badge colorScheme="red">abaixo do mínimo</Badge>}
                          </HStack>
                        ),
                      },
                      {
                        label: "Lista",
                        render: (p) => (p.lista_compra_id ? (
                          <Button size="xs" variant="link" colorScheme="orange" rightIcon={<ExternalLink size={12} />}
                            onClick={() => navigate(`/listas-compras/${p.lista_compra_id}`)}>
                            #{p.lista_compra_id}
                          </Button>
                        ) : "—"),
                      },
                      { label: "Vendas", numerico: true, render: (p) => `${p.itens_venda} · ${formatBRL(p.valor_vendas)}` },
                      { label: "Reposição", numerico: true, render: (p) => `${p.unidades_complemento} un · ${formatBRL(p.valor_complemento)}` },
                      { label: "Total", numerico: true, render: (p) => <Text fontWeight="bold">{formatBRL(p.valor_total)}</Text> },
                      { label: "Resposta", render: (p) => p.lista_status || "—" },
                      { label: "Erro", quebra: true, render: (p) => (p.erro ? <Text color="red.500" fontSize="xs" noOfLines={2} minW="160px">{p.erro}</Text> : "—") },
                    ]}
                    linhas={dados.pedidos}
                    vazio="Nenhum pedido semanal gerado ainda."
                  />
                </TabPanel>
              </TabPanels>
            </Tabs>
          </Box>
        </>
      )}

      <AlertDialog isOpen={!!confirmar} leastDestructiveRef={cancelRef} onClose={() => setConfirmar(null)} isCentered>
        <AlertDialogOverlay>
          <AlertDialogContent mx={3}>
            <AlertDialogHeader fontSize="md">Confirmar</AlertDialogHeader>
            <AlertDialogBody>
              <VStack align="stretch" spacing={2}>
                {(confirmar || []).map((t, i) => <Text key={i} fontSize="sm">{t}</Text>)}
              </VStack>
            </AlertDialogBody>
            <AlertDialogFooter gap={2}>
              <Button ref={cancelRef} size="sm" onClick={() => setConfirmar(null)}>Cancelar</Button>
              <Button size="sm" colorScheme="green" onClick={salvar}>Salvar e ligar</Button>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialogOverlay>
      </AlertDialog>
    </Box>
  );
};

export default ComprasFabrica;
